// View rules for an integration's pinned context card (API-08, sheet rows BOX-10 and API-08). The card is
// untrusted text from another system: it is cleaned to plain text, links are https only and always show
// their host, and the optional pop-out window opens the first https link. ContextCard.tsx renders it.
import { CONTEXT_CARD_TALKING_POINT_MAX, DEFAULT_CONTEXT_CARD_DISPOSITIONS, type Call, type CallContextCard, type Capability } from '@bcio-voice/contracts';
import { LIVE_STATES } from '../../call/callStore.ts';

/** Line breaks and tabs become spaces. */
const BREAKS = /[\t\n\v\f\r\u0085\u2028\u2029]+/g;
/** C0 and C1 controls, DEL, zero-width space, direction marks and overrides, invisible joiners and the BOM:
 * characters that can hide or reorder what a label appears to say. ZWNJ and ZWJ stay for scripts that need them. */
const INVISIBLE = /[\u0000-\u001F\u007F-\u009F\u061C\u200B\u200E\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g;

export function cleanText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value
    .replace(BREAKS, ' ')
    .replace(INVISIBLE, '')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u00B7\u2022]/g, ',')
    .replace(/ {2,}/g, ' ')
    .trim();
}

export interface SafeLink { href: string; host: string }

/** An https address with a host and no user name or password; anything else is not offered as a link. */
export function safeLink(url: unknown): SafeLink | null {
  if (typeof url !== 'string' || url.length > 2048) return null;
  let parsed: URL;
  try { parsed = new URL(url.trim()); } catch { return null; }
  if (parsed.protocol !== 'https:' || !parsed.hostname || parsed.username || parsed.password) return null;
  // URL serialises an international host in its ASCII (punycode) form, so a look-alike host shows as what it is.
  return { href: parsed.href, host: parsed.host };
}

export interface ContextField { label: string; value: string }
export interface ContextLink extends SafeLink { label: string }
export interface ContextWho {
  company: string;
  contactName: string | null;
  contactRole: string | null;
  stage: string | null;
  disposition: string | null;
  owner: string | null;
}
export interface ContextObjection { objection: string; response: string }
export interface ContextFact { label: string; value: string }
export interface ContextHistoryEntry { type: string; summary: string; at: string | null }
export interface DispositionOption { id: string; label: string }

/** Provisional: the CRM files it by itself for an outbound call answered with 15 seconds or more of talk. The call
 * connected, but only the rep knows how it went, so it shows as the current value until the rep picks a real one. */
export const CONNECTED_CONFIRM_OUTCOME = 'connected_confirm_outcome';

/** The contract's list (packages/contracts DEFAULT_CONTEXT_CARD_DISPOSITIONS), so the wrap-up the API writes suggests
 * from exactly the options this pane shows. */
export const DEFAULT_DISPOSITIONS: readonly DispositionOption[] = DEFAULT_CONTEXT_CARD_DISPOSITIONS;

export type ContextCardKind = 'candidate' | 'unknown' | 'generic';

export interface ContextCardModel {
  kind: ContextCardKind;
  title: string;
  /** The integration (API key) name, or "an integration" when the key has no name. */
  source: string;
  updatedAt: string;
  version: number;
  fields: ContextField[];
  /** https links in the card's order; the first is the primary link that "Pop out" opens. */
  links: ContextLink[];
  /** Links the card sent that are not shown because they are not a usable https address. */
  refusedLinks: number;
  candidateId: string | null;
  who: ContextWho | null;
  /** The cheat sheet's short lines, shown first so they can be read while the call rings. Null on cards without them. */
  talkingPoints: string[] | null;
  opener: string | null;
  whatToSay: string[] | null;
  objections: ContextObjection[] | null;
  facts: ContextFact[] | null;
  history: ContextHistoryEntry[] | null;
  nextStep: string | null;
  dispositions: DispositionOption[];
}

const CONSOLIDATED_KEYS = ['who', 'candidate_id', 'talking_points', 'opener', 'what_to_say', 'objections', 'facts', 'history', 'next_step', 'dispositions'] as const;

function cleanNullable(value: unknown): string | null {
  const s = cleanText(value);
  return s ? s : null;
}

/** A glance list: at most this many talking points are shown, in the card's order. */
export const TALKING_POINTS_SHOWN = 5;

/** Cuts to `max` characters, ending in "..." when cut, never inside a surrogate pair. */
function clipText(text: string, max: number): string {
  if (text.length <= max) return text;
  let end = Math.max(0, max - 3);
  const code = text.charCodeAt(end - 1);
  if (code >= 0xd800 && code <= 0xdbff) end -= 1;
  return `${text.slice(0, end).trimEnd()}...`;
}

/** Talking points as plain text: strings only, cleaned, empty and repeated lines dropped, each cut to the contract's
 * length and the list to TALKING_POINTS_SHOWN. Null when the card has none (older cards and older CRMs send none). */
export function talkingPointsOf(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const points: string[] = [];
  for (const item of value) {
    const text = clipText(cleanText(item), CONTEXT_CARD_TALKING_POINT_MAX);
    if (!text || points.includes(text)) continue;
    points.push(text);
    if (points.length >= TALKING_POINTS_SHOWN) break;
  }
  return points.length ? points : null;
}

export function contextCardModel(card: CallContextCard): ContextCardModel {
  const fields: ContextField[] = [];
  for (const f of Array.isArray(card.fields) ? card.fields : []) {
    const label = cleanText(f?.label), value = cleanText(f?.value);
    if (label || value) fields.push({ label: label || 'Detail', value });
  }
  const rawTitle = cleanText(card.title);
  const title = rawTitle || 'Context card';
  const isUnknown = card.match === 'none' || /^not in (?:the )?(?:crm|pipeline)$/i.test(rawTitle);
  const hasConsolidated = CONSOLIDATED_KEYS.some(k => card[k] !== undefined && card[k] !== null);
  const kind: ContextCardKind = isUnknown ? 'unknown' : (card.match === 'candidate' || hasConsolidated ? 'candidate' : 'generic');
  const links: ContextLink[] = [];
  let refusedLinks = 0;
  for (const l of Array.isArray(card.links) ? card.links : []) {
    const safe = safeLink(l?.url);
    if (!safe) { refusedLinks += 1; continue; }
    const label = cleanText(l?.label);
    const fallbackLabel = isUnknown ? 'Create candidate' : 'Link';
    const normalizedLabel = isUnknown && /^open (?:pipeline|crm)$/i.test(label)
      ? 'Create candidate'
      : (label && label.toLowerCase() !== safe.host.toLowerCase() ? label : fallbackLabel);
    links.push({ ...safe, label: normalizedLabel });
  }

  const rawWho = card.who;
  const who: ContextWho | null = rawWho && typeof rawWho === 'object' && cleanText(rawWho.company)
    ? {
        company: cleanText(rawWho.company),
        contactName: cleanNullable(rawWho.contact_name),
        contactRole: cleanNullable(rawWho.contact_role),
        stage: cleanNullable(rawWho.stage),
        disposition: cleanNullable(rawWho.disposition),
        owner: cleanNullable(rawWho.owner),
      }
    : (kind === 'candidate' ? { company: title, contactName: null, contactRole: null, stage: null, disposition: null, owner: null } : null);

  const opener = cleanNullable(card.opener);
  const whatToSay = Array.isArray(card.what_to_say)
    ? card.what_to_say.map(cleanText).filter(Boolean)
    : null;
  const objections = Array.isArray(card.objections)
    ? card.objections
        .map(o => ({ objection: cleanText(o?.objection), response: cleanText(o?.response) }))
        .filter(o => o.objection && o.response)
    : null;
  const facts = Array.isArray(card.facts)
    ? card.facts
        .map(f => ({ label: cleanText(f?.label), value: cleanText(f?.value) }))
        .filter(f => f.label && f.value)
    : null;
  const history = Array.isArray(card.history)
    ? card.history
        .map(h => ({ type: cleanText(h?.type) || 'event', summary: cleanText(h?.summary), at: cleanNullable(h?.at) }))
        .filter(h => h.summary)
        .slice(0, 3)
    : null;
  const nextStep = cleanNullable(card.next_step);
  const customDispositions = Array.isArray(card.dispositions)
    ? card.dispositions
        .map(d => ({ id: cleanText(d?.id), label: cleanText(d?.label) }))
        .filter(d => /^[a-z0-9_:-]+$/.test(d.id) && d.label)
    : [];
  const dispositions = customDispositions.length ? customDispositions : [...DEFAULT_DISPOSITIONS];

  return {
    kind,
    title: isUnknown ? 'Not in the CRM' : title,
    source: cleanText(card.source?.name) || 'an integration',
    updatedAt: typeof card.updated_at === 'string' ? card.updated_at : '',
    version: card.version,
    fields,
    links,
    refusedLinks,
    candidateId: cleanNullable(card.candidate_id),
    who,
    talkingPoints: talkingPointsOf((card as { talking_points?: unknown }).talking_points),
    opener,
    whatToSay: whatToSay && whatToSay.length ? whatToSay : null,
    objections: objections && objections.length ? objections : null,
    facts: facts && facts.length ? facts : null,
    history: history && history.length ? history : null,
    nextStep,
    dispositions,
  };
}

export interface DispositionRequestSpec {
  method: 'POST';
  path: string;
  body: { disposition: string; candidate_id: string | null };
}

/** Builds the one-click CRM ledger disposition write-back request. */
export function dispositionRequest(callId: string, disposition: string, candidateId: string | null = null): DispositionRequestSpec {
  return {
    method: 'POST',
    path: `/calls/${encodeURIComponent(callId)}/context/disposition`,
    body: {
      disposition: cleanText(disposition),
      candidate_id: candidateId ? cleanText(candidateId) : null,
    },
  };
}

/** Whether a newly arrived candidate screen pop should automatically open and focus on a live call (ring or dial). */
export function shouldAutoFocusPop({ seen, callId, card, callState }: Omit<AutoPopInput, 'enabled'>): AutoPopDecision {
  if (!card || typeof card.version !== 'number') return { key: null, fresh: false, pop: false };
  const key = cardKey(callId, card.version);
  if (seen.has(key)) return { key, fresh: false, pop: false };
  const model = contextCardModel(card);
  return { key, fresh: true, pop: LIVE_STATES.has(callState) && model.kind === 'candidate' };
}

/** More fields than this and the card starts collapsed to the first four, with a button naming how many more. */
export const COLLAPSED_FIELDS = 4;
export const isCollapsible = (model: ContextCardModel): boolean => model.fields.length > COLLAPSED_FIELDS;
export function shownFields(model: ContextCardModel, expanded: boolean): ContextField[] {
  return expanded || !isCollapsible(model) ? model.fields : model.fields.slice(0, COLLAPSED_FIELDS);
}
export function toggleText(model: ContextCardModel, expanded: boolean): string {
  if (expanded) return 'Show fewer details';
  const more = model.fields.length - COLLAPSED_FIELDS;
  return `Show ${more} more ${more === 1 ? 'detail' : 'details'}`;
}

export function refusedLinksText(count: number): string {
  return count === 1
    ? 'One link isn’t shown because it isn’t a secure https address.'
    : `${count} links aren’t shown because they aren’t secure https addresses.`;
}

// ---- Before the CRM's card arrives (ring time) ----
// The CRM pins its card after Sidetone's call.created webhook reaches it: about 1.5 s after the call starts while the CRM is
// healthy, minutes when it is stalled. The phone never waits for it. Until the call's own card arrives the pop shows the card
// from the last call with this number (GET /calls/{id}/context/previous), or says it is still looking, then that it has none.

/** How long the pop says it is looking before it says the card is not here yet. */
export const CARD_WAIT_MS = 6000;
const AWAITED_STATES: ReadonlySet<Call['state']> = new Set([...LIVE_STATES, 'dropping']);
export type AwaitedCardState = 'loading' | 'unavailable';

/** A live call with an outside party, in a workspace whose CRM call webhooks are on: a CRM card is expected. */
export function cardExpected(call: Pick<Call, 'state' | 'direction' | 'destination'>, crm: Capability | null | undefined): boolean {
  if (!crm || (crm.status !== 'enabled' && crm.status !== 'degraded')) return false;
  if (!AWAITED_STATES.has(call.state)) return false;
  return call.direction === 'inbound' || call.destination.kind === 'pstn';
}

/** 'loading' for CARD_WAIT_MS from the call's start, then 'unavailable'. A start time that cannot be read counts as late. */
export function awaitedCardState(call: Pick<Call, 'created_at'>, now: number): AwaitedCardState {
  const start = Date.parse(call.created_at);
  return Number.isFinite(start) && now - start < CARD_WAIT_MS ? 'loading' : 'unavailable';
}

/** Milliseconds until 'loading' turns into 'unavailable' (0 once it has). */
export function awaitedCardRemainingMs(call: Pick<Call, 'created_at'>, now: number): number {
  const start = Date.parse(call.created_at);
  return Number.isFinite(start) ? Math.max(0, start + CARD_WAIT_MS - now) : 0;
}

export function awaitedCardText(state: AwaitedCardState, showingPrevious: boolean): string {
  if (showingPrevious) {
    return state === 'loading'
      ? 'Checking the CRM for this call’s card. Until it arrives, this is the card from the last call with this number.'
      : 'The CRM hasn’t sent this call’s card yet. This is the card from the last call with this number; your call isn’t affected.';
  }
  return state === 'loading'
    ? 'Looking up this number in the CRM…'
    : 'The CRM card isn’t here yet. Your call isn’t affected; the card appears here as soon as the CRM sends it.';
}

export interface PreviousCardResult { card: CallContextCard | null; fromCallAt: string | null }
const NO_PREVIOUS: PreviousCardResult = Object.freeze({ card: null, fromCallAt: null });

export const previousCardPath = (callId: string): string => `/calls/${encodeURIComponent(callId)}/context/previous`;

/** Reads the {data: {card, from_call_at}} answer; anything else (an API without the route, a malformed body) is "none". */
export function readPreviousCard(body: unknown): PreviousCardResult {
  const data = body && typeof body === 'object' ? (body as { data?: unknown }).data : null;
  if (!data || typeof data !== 'object') return NO_PREVIOUS;
  const { card, from_call_at: at } = data as { card?: unknown; from_call_at?: unknown };
  if (!card || typeof card !== 'object' || typeof (card as CallContextCard).version !== 'number') return NO_PREVIOUS;
  if (contextCardModel(card as CallContextCard).kind === 'unknown') return NO_PREVIOUS;
  return { card: card as CallContextCard, fromCallAt: typeof at === 'string' ? at : null };
}

/** One request per call per tab, shared by the ringing and the answered card. A failure is remembered as "none" so a CRM
 * outage never turns into repeated requests; the call's own card still arrives through the event stream. */
export class PreviousCards {
  private readonly entries = new Map<string, { result: PreviousCardResult | null; pending: Promise<PreviousCardResult> }>();
  constructor(private readonly limit = 100) {}
  peek(callId: string): PreviousCardResult | null { return this.entries.get(callId)?.result ?? null; }
  load(callId: string, fetchBody: () => Promise<unknown>): Promise<PreviousCardResult> {
    const known = this.entries.get(callId);
    if (known) return known.pending;
    const entry: { result: PreviousCardResult | null; pending: Promise<PreviousCardResult> } = { result: null, pending: Promise.resolve(NO_PREVIOUS) };
    entry.pending = Promise.resolve()
      .then(fetchBody)
      .then(readPreviousCard, () => NO_PREVIOUS)
      .then(result => { entry.result = result; return result; });
    this.entries.set(callId, entry);
    while (this.entries.size > this.limit) {
      const oldest = this.entries.keys().next().value;
      if (oldest === undefined) break;
      this.entries.delete(oldest);
    }
    return entry.pending;
  }
}
export const previousCards = new PreviousCards();

/** The line above a card carried over from an earlier call. */
export function carriedCardText(fromCallAt: string | null, now: number, formatWhen: (iso: string, now: number) => string): string {
  const when = fromCallAt ? formatWhen(fromCallAt, now) : '';
  return when ? `From the last call with this number (${when}).` : 'From the last call with this number.';
}

// ---- Pop-out window ----

export const POP_WINDOW_NAME = 'sidetone-pipeline';
/** No "noopener" in the feature string: with it, window.open returns null even when the window opened (HTML
 * standard, window open steps), so a blocked pop-up could not be told apart from an open one. popOut() cuts the
 * opener itself straight after opening, before the new page can load and run. */
export const POP_FEATURES = 'popup=yes,width=1100,height=800';
export type PopResult = 'opened' | 'blocked' | 'refused';
export type WindowOpener = (url: string, target: string, features: string) => Window | null;

export function popOut(url: string, open: WindowOpener | null, focus = false): PopResult {
  const link = safeLink(url);
  if (!link) return 'refused';
  if (!open) return 'blocked';
  let win: Window | null = null;
  try { win = open(link.href, POP_WINDOW_NAME, POP_FEATURES); } catch { win = null; }
  if (!win) return 'blocked';
  // A window reused by name may already show another origin; its opener was cut when it first opened.
  try { win.opener = null; } catch { /* cross-origin: nothing to cut */ }
  if (focus) { try { win.focus(); } catch { /* the browser decides */ } }
  return 'opened';
}

export function browserOpener(): WindowOpener | null {
  if (typeof window === 'undefined' || typeof window.open !== 'function') return null;
  return (url, target, features) => window.open(url, target, features);
}

export function blockedText(pageHost: string | null | undefined): string {
  return `Your browser blocked the pop-up. Allow pop-ups for ${pageHost || 'sidet.one'}, or use the link.`;
}

// ---- Automatic pop-out ----

export const AUTO_POP_KEY = 'sidetone.contextCard.autoPop';
type PreferenceStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function browserStorage(): PreferenceStorage | null {
  try { return typeof window === 'undefined' ? null : window.localStorage; } catch { return null; }
}

/** Off until this browser turns it on: the call window carries the card, so the CRM's own page is opt-in
 * (docs/design/CALL-POP-KRISP-BAR.md lane 6, October 31, 2026; it was on by default from October 30). */
export const AUTO_POP_DEFAULT = false;

/** Per browser. Only an explicit '1' (this browser turned it on) reads as on; nothing stored, '0', anything else and
 * blocked storage read as off. */
export function readAutoPop(storage: PreferenceStorage | null = browserStorage()): boolean {
  try {
    const stored = storage?.getItem(AUTO_POP_KEY);
    return stored === '1' ? true : stored === '0' ? false : AUTO_POP_DEFAULT;
  } catch { return AUTO_POP_DEFAULT; }
}

export function writeAutoPop(on: boolean, storage: PreferenceStorage | null = browserStorage()): void {
  try {
    if (on) storage?.setItem(AUTO_POP_KEY, '1');
    else storage?.setItem(AUTO_POP_KEY, '0');
  } catch { /* Storage blocked: the choice lasts until the page reloads. */ }
}

/** One shared value for every mounted card, kept in step with other tabs through the storage event. */
export function createAutoPopPreference(storage: () => PreferenceStorage | null = browserStorage) {
  let cached: boolean | null = null;
  const listeners = new Set<() => void>();
  const get = (): boolean => (cached ??= readAutoPop(storage()));
  const set = (on: boolean): void => {
    cached = on;
    writeAutoPop(on, storage());
    for (const l of [...listeners]) l();
  };
  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    const onStorage = (e: StorageEvent) => { if (e.key === AUTO_POP_KEY || e.key === null) { cached = null; listener(); } };
    if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
    return () => {
      listeners.delete(listener);
      if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
    };
  };
  return { get, set, subscribe };
}
export const autoPopPreference = createAutoPopPreference();

export const cardKey = (callId: string, version: number): string => `${callId}:${version}`;

export interface AutoPopInput {
  /** Card versions this tab has already shown (keys from cardKey). */
  seen: { has(key: string): boolean };
  callId: string;
  card: CallContextCard | null;
  callState: Call['state'];
  enabled: boolean;
}
export interface AutoPopDecision {
  /** The key to remember; null when there is no card. */
  key: string | null;
  /** First time this tab sees this card version for this call. */
  fresh: boolean;
  pop: boolean;
}

/** Pops at most once per call and card version, only while the call is live, only when this browser opted in,
 * and only when the card has an https link. A version seen while the preference was off is never popped later. */
export function autoPopDecision({ seen, callId, card, callState, enabled }: AutoPopInput): AutoPopDecision {
  if (!card || typeof card.version !== 'number') return { key: null, fresh: false, pop: false };
  const key = cardKey(callId, card.version);
  if (seen.has(key)) return { key, fresh: false, pop: false };
  const hasLink = (Array.isArray(card.links) ? card.links : []).some(l => safeLink(l?.url) !== null);
  return { key, fresh: true, pop: enabled && LIVE_STATES.has(callState) && hasLink };
}

/** The card versions this tab has shown, oldest dropped first. A page reload starts empty. */
export class SeenCards {
  private readonly keys = new Set<string>();
  constructor(private readonly limit = 200) {}
  has(key: string): boolean { return this.keys.has(key); }
  add(key: string): void {
    this.keys.delete(key);
    this.keys.add(key);
    while (this.keys.size > this.limit) {
      const oldest = this.keys.values().next().value;
      if (oldest === undefined) break;
      this.keys.delete(oldest);
    }
  }
}
export const seenCards = new SeenCards();
